import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  LayoutDashboard, Heart, FileText, Pill, TestTube, Calendar,
  Wallet, ShoppingCart, MessageSquare, Bell, Settings, LogOut,
  ChevronRight, Plus, Search, RefreshCw, User, Phone, Mail,
  MapPin, Gift, Copy, Share2, Crown, Clock, CheckCircle, XCircle, Loader2, Save, Download, Eye, Filter,
  Stethoscope, HelpCircle, Building2, Send, AlertCircle, Video, Camera, ArrowLeft, Apple,
  HeartPulse, ShoppingBag, UserCircle2, QrCode, MessageCircle
} from 'lucide-react';
import { openPrescriptionPrint } from '../components/PrescriptionTemplate';
import { openInvoicePrint } from '../components/InvoiceTemplate';
import NotificationBell from '../components/NotificationBell';
import { requestNotificationPermission, registerPushNotifications } from '../lib/pushNotifications';
import { GroupedPortalNav } from '../components/ui/grouped-portal-nav';
import SavedAddresses from './SavedAddresses';
import PatientFaceVitalsTab from './PatientFaceVitalsTab';
import PatientHealthTracking from './PatientHealthTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 3-tab grouped navigation (pure UI wrapper over the existing 11 tabs - nothing removed, nothing added to backend)
const PATIENT_NAV_GROUPS = [
  {
    id: 'medical_care', label: 'Medical Care', icon: HeartPulse,
    subItems: [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      { id: 'face-vitals', label: 'Face Vitals', icon: Camera },
      { id: 'health-tracking', label: 'Health Trends', icon: Apple },
      { id: 'prescriptions', label: 'Prescriptions', icon: Pill },
      { id: 'lab-reports', label: 'Lab Logs', icon: TestTube },
    ]
  },
  {
    id: 'purchases_visits', label: 'Purchases & Visits', icon: ShoppingBag,
    subItems: [
      { id: 'visits', label: 'Visits', icon: Stethoscope },
      { id: 'appointments', label: 'Appointments', icon: Calendar },
      { id: 'bills', label: 'Digital Invoices', icon: FileText },
      { id: 'orders', label: 'Pharmacy Orders', icon: ShoppingCart },
      { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag, path: '/patient/marketplace' },
    ]
  },
  {
    id: 'my_account', label: 'My Account', icon: UserCircle2,
    subItems: [
      { id: 'profile', label: 'Profile Settings', icon: User },
      { id: 'support', label: 'Help Desk', icon: HelpCircle },
    ]
  }
];

export default function PatientDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTabRaw] = useState(initialTab);
  const setActiveTab = useCallback((val) => {
    setActiveTabRaw(val);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'overview') next.delete('tab');
      else next.set('tab', val);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [subscription, setSubscription] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [visits, setVisits] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [labReports, setLabReports] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [bills, setBills] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // New dialogs state
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [showPayBillDialog, setShowPayBillDialog] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Doctor search state
  const [doctorSearch, setDoctorSearch] = useState('');
  const [searchingDoctors, setSearchingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  
  // Appointment form
  const [appointmentDoctor, setAppointmentDoctor] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentReason, setAppointmentReason] = useState('');
  
  // Lab reports filter
  const [labReportDateFrom, setLabReportDateFrom] = useState('');
  const [labReportDateTo, setLabReportDateTo] = useState('');
  
  // Visits filter
  const [visitsDateFrom, setVisitsDateFrom] = useState('');
  const [visitsDateTo, setVisitsDateTo] = useState('');
  
  // Orders filter
  const [ordersDateFrom, setOrdersDateFrom] = useState('');
  const [ordersDateTo, setOrdersDateTo] = useState('');
  const [ordersSearch, setOrdersSearch] = useState('');
  
  // Edit profile form
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editDob, setEditDob] = useState('');
  const [editGender, setEditGender] = useState('');
  const [editBloodGroup, setEditBloodGroup] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editEmergencyContact, setEditEmergencyContact] = useState('');

  // Support state
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportDoctors, setSupportDoctors] = useState([]);
  const [supportVendors, setSupportVendors] = useState([]);
  const [platformSupport, setPlatformSupport] = useState(null);
  const [supportSearch, setSupportSearch] = useState('');
  const [supportFilter, setSupportFilter] = useState('all'); // all, doctors, vendors
  const [supportVendorType, setSupportVendorType] = useState('all');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketPriority, setTicketPriority] = useState('normal');
  const [showTicketDetail, setShowTicketDetail] = useState(null);
  const [ticketReply, setTicketReply] = useState('');

  const getToken = () => localStorage.getItem('patient_token');

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/patient/login');
      return;
    }

    try {
      // First verify auth and get profile (critical - must succeed)
      const profileRes = await fetch(`${API_URL}/api/patient-auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!profileRes.ok) throw new Error('Unauthorized');
      const profileData = await profileRes.json();
      setPatient(profileData);
      setLoading(false); // Show UI immediately after profile loads

      // Fetch all other data in parallel (non-blocking)
      const [walletRes, subRes, refRes, visitsRes, rxRes, labRes, apptRes, billsRes, ordersRes, doctorsRes] = await Promise.all([
        fetch(`${API_URL}/api/patient-wallet/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-subscription/my-subscription`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-referral/my-code`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/visits?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/prescriptions?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/lab-reports?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/appointments?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/bills?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-marketplace/orders?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-health/all-doctors`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);

      // Process responses
      if (walletRes?.ok) setWallet(await walletRes.json());
      if (subRes?.ok) { const d = await subRes.json(); setSubscription(d.subscription); }
      if (refRes?.ok) { const d = await refRes.json(); setReferralCode(d.code); }
      if (visitsRes?.ok) { const d = await visitsRes.json(); setVisits(d.visits || []); }
      if (rxRes?.ok) { const d = await rxRes.json(); setPrescriptions(d.prescriptions || []); }
      if (labRes?.ok) { const d = await labRes.json(); setLabReports(d.reports || []); }
      if (apptRes?.ok) { const d = await apptRes.json(); setAppointments(d.appointments || []); }
      if (billsRes?.ok) { const d = await billsRes.json(); setBills(d.bills || []); }
      if (ordersRes?.ok) { const d = await ordersRes.json(); setOrders(d.orders || []); }
      if (doctorsRes?.ok) { const d = await doctorsRes.json(); setDoctors(d.doctors || []); }

    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Session expired. Please login again.');
      localStorage.removeItem('patient_token');
      localStorage.removeItem('patient_user');
      navigate('/patient/login');
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    // Register push notifications
    requestNotificationPermission().then(perm => {
      if (perm === 'granted') registerPushNotifications('patient');
    });
  }, [fetchData]);

  // Auto-refresh patient data every 20s
  const patientPollRef = useRef(null);
  useEffect(() => {
    patientPollRef.current = setInterval(() => { if (!document.hidden) fetchData(); }, 20000);
    const onVis = () => { if (!document.hidden) fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(patientPollRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('patient_token');
    localStorage.removeItem('patient_user');
    toast.success('Logged out successfully');
    navigate('/patient/login');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const shareReferralCode = async () => {
    const shareUrl = `${window.location.origin}/patient/login?ref=${referralCode}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: '1Clin Patient Portal',
          text: `Join 1Clin using my referral code: ${referralCode}`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyReferralCode();
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied!');
    }
  };
  
  // Search doctors
  const searchDoctors = async (searchTerm) => {
    setSearchingDoctors(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-health/all-doctors?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setDoctors(data.doctors || []);
    } catch (err) {
      console.error('Failed to search doctors', err);
    } finally {
      setSearchingDoctors(false);
    }
  };
  
  // Fetch all doctors on dialog open
  const fetchDoctorsForAppointment = async () => {
    setSearchingDoctors(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-health/all-doctors`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setDoctors(data.doctors || []);
    } catch (err) {
      console.error('Failed to fetch doctors', err);
    } finally {
      setSearchingDoctors(false);
    }
  };
  
  // Get day name from date
  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  };
  
  // Get clinic timing for selected day
  const getTimingForDay = (doctor, dateStr) => {
    if (!doctor?.clinic_timings || !dateStr) return null;
    const dayName = getDayName(dateStr);
    return doctor.clinic_timings.find(t => t.day === dayName);
  };

  // Generate time slots from clinic timings (30-min intervals)
  const generateTimeSlots = (doctor, dateStr) => {
    const timing = getTimingForDay(doctor, dateStr);
    if (!timing || !timing.is_open) return [];
    
    const slots = [];
    const addSlots = (start, end, session) => {
      if (!start || !end) return;
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      let h = sh, m = sm;
      while (h < eh || (h === eh && m < em)) {
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        slots.push({ time: timeStr, session });
        m += 30;
        if (m >= 60) { h++; m -= 60; }
      }
    };
    
    addSlots(timing.morning_start, timing.morning_end, 'Morning');
    addSlots(timing.evening_start, timing.evening_end, 'Evening');
    if (timing.extra_slots) {
      timing.extra_slots.forEach((es, i) => addSlots(es.start, es.end, `Slot ${i + 3}`));
    }
    return slots;
  };
  
  // Request new appointment
  const handleRequestAppointment = async () => {
    if (!appointmentDoctor || !appointmentDate || !appointmentReason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate clinic is open on selected day
    if (selectedDoctor) {
      const timing = getTimingForDay(selectedDoctor, appointmentDate);
      if (timing && !timing.is_open) {
        toast.error('Clinic is closed on the selected day. Please pick another date.');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-health/appointments/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          doctor_id: appointmentDoctor,
          preferred_date: appointmentDate,
          preferred_time: appointmentTime || '09:00',
          reason: appointmentReason
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Appointment request submitted!');
        setShowAppointmentDialog(false);
        setAppointmentDoctor('');
        setAppointmentDate('');
        setAppointmentTime('');
        setAppointmentReason('');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to request appointment');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setSubmitting(false);
  };
  
  // Cancel appointment
  const handleCancelAppointment = async (apptId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      const res = await fetch(`${API_URL}/api/patient-health/appointments/${apptId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('Appointment cancelled');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to cancel appointment');
      }
    } catch { toast.error('Network error'); }
  };

  // Cancel order with wallet refund
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order? The amount will be refunded to your wallet.')) return;
    try {
      const res = await fetch(`${API_URL}/api/patient-marketplace/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Order cancelled and refunded');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to cancel order');
      }
    } catch { toast.error('Network error'); }
  };

  // Open edit profile dialog
  const openEditProfile = () => {
    if (patient) {
      setEditName(patient.name || '');
      setEditEmail(patient.email || '');
      setEditDob(patient.date_of_birth || '');
      setEditGender(patient.gender || '');
      setEditBloodGroup(patient.blood_group || '');
      setEditAddress(patient.address || '');
      setEditEmergencyContact(patient.emergency_contact || '');
    }
    setShowEditProfileDialog(true);
  };
  
  // Save profile
  const handleSaveProfile = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          name: editName || undefined,
          email: editEmail || undefined,
          date_of_birth: editDob || undefined,
          gender: editGender || undefined,
          blood_group: editBloodGroup || undefined,
          address: editAddress || undefined,
          emergency_contact: editEmergencyContact || undefined
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success('Profile updated successfully!');
        setShowEditProfileDialog(false);
        setPatient(data);
      } else {
        toast.error(data.detail || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setSubmitting(false);
  };
  
  // Pay bill
  const handlePayBill = async () => {
    if (!selectedBill) return;
    
    const amountDue = selectedBill.total - (selectedBill.amount_paid || 0);
    if (wallet.balance < amountDue) {
      toast.error('Insufficient wallet balance');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-payments/pay-invoice/${selectedBill.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Invoice paid! ₹${data.amount_paid} deducted from wallet.`);
        setShowPayBillDialog(false);
        setSelectedBill(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to pay invoice');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setSubmitting(false);
  };

  // Instant UPI Pay - opens device UPI app (Google Pay/PhonePe/Paytm) via deep link, no gateway
  const handleInstantUpiPay = (bill) => {
    const upiId = bill.clinic_settings?.clinic_upi_id;
    const amountDue = (bill.total - (bill.amount_paid || 0)).toFixed(2);
    if (!upiId) {
      toast.error('This clinic has not set up UPI payments yet. Please use Pay via Wallet instead.');
      return;
    }
    const payeeName = bill.clinic_name || bill.doctor_name || '1Clin Clinic';
    const note = `Invoice ${bill.invoice_number || ''}`.trim();
    const upiLink = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(payeeName)}&am=${amountDue}&cu=INR&tn=${encodeURIComponent(note)}`;
    window.location.href = upiLink;
    toast.info('Opening your UPI app... After paying, the doctor will mark this bill as paid.');
  };

  // Support functions
  const fetchSupportData = async () => {
    const token = getToken();
    try {
      const [ticketsRes, doctorsRes, vendorsRes, contactRes] = await Promise.all([
        fetch(`${API_URL}/api/support/tickets`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/support/public/doctors`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/support/public/vendors`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_URL}/api/admin/public/support-contacts/doctor`).then(r => r.ok ? r.json() : null).catch(() => null)
      ]);
      setSupportTickets(ticketsRes);
      setSupportDoctors(doctorsRes);
      setSupportVendors(vendorsRes);
      if (contactRes) setPlatformSupport(contactRes);
    } catch (e) { console.error('Support fetch error:', e); }
  };

  const handleCreateSupportTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    if (!ticketCategory) {
      toast.error('Please select an Issue Category');
      return;
    }
    try {
      setSubmitting(true);
      const token = getToken();
      const res = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject: ticketSubject, message: ticketMessage, category: ticketCategory, priority: ticketPriority })
      });
      if (!res.ok) throw new Error('Failed to create ticket');
      toast.success('Support ticket created successfully');
      setShowCreateTicket(false);
      setTicketSubject(''); setTicketMessage(''); setTicketCategory(''); setTicketPriority('normal');
      fetchSupportData();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleReplyTicket = async (ticketId) => {
    if (!ticketReply.trim()) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: ticketReply })
      });
      if (!res.ok) throw new Error('Failed to reply');
      const updated = await res.json();
      setShowTicketDetail(updated);
      setTicketReply('');
      fetchSupportData();
      toast.success('Reply sent');
    } catch (e) { toast.error(e.message); }
  };

  const filteredSupportItems = () => {
    const search = supportSearch.toLowerCase();
    let items = [];
    if (supportFilter === 'all' || supportFilter === 'doctors') {
      items = [...items, ...supportDoctors.map(d => ({...d, _type: 'doctor'}))];
    }
    if (supportFilter === 'all' || supportFilter === 'vendors') {
      let filtered = supportVendors;
      if (supportVendorType !== 'all') filtered = filtered.filter(v => v.vendor_type === supportVendorType);
      items = [...items, ...filtered.map(v => ({...v, _type: 'vendor'}))];
    }
    if (search) {
      items = items.filter(i => 
        (i.name || '').toLowerCase().includes(search) ||
        (i.clinic_name || '').toLowerCase().includes(search) ||
        (i.specialty || '').toLowerCase().includes(search) ||
        (i.vendor_type || '').toLowerCase().includes(search)
      );
    }
    return items;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              1Clin
            </div>
            {subscription && (
              <Badge className="bg-amber-100 text-amber-800">
                <Crown className="h-3 w-3 mr-1" />
                {subscription.plan_name}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = 'https://immuski.com'}
              className="text-violet-600 border-violet-200 hover:bg-violet-50 gap-1 hidden sm:flex"
              data-testid="back-to-immuski-btn"
            >
              <ArrowLeft className="h-3 w-3" /> Immuski
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/patient/wallet')}
              className="gap-2"
            >
              <Wallet className="h-4 w-4" />
              ₹{wallet.balance?.toFixed(0) || 0}
            </Button>
            
            <NotificationBell portalType="patient" />
            
            <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon">
                  <LogOut className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Logout</DialogTitle>
                </DialogHeader>
                <p className="text-muted-foreground">Are you sure you want to logout?</p>
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={() => setShowLogoutDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleLogout} className="flex-1 bg-red-600 hover:bg-red-700">
                    Logout
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 overflow-x-hidden">
        {/* Welcome Section */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            Welcome, {patient?.name || 'Patient'}
          </h1>
          <p className="text-muted-foreground text-sm">Manage your health records and appointments</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('visits')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Heart className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{visits.length}</p>
                  <p className="text-xs text-muted-foreground">Visits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('appointments')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{appointments.length}</p>
                  <p className="text-xs text-muted-foreground">Appointments</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('prescriptions')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Pill className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{prescriptions.length}</p>
                  <p className="text-xs text-muted-foreground">Prescriptions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('lab-reports')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <TestTube className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{labReports.length}</p>
                  <p className="text-xs text-muted-foreground">Lab Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        {/* Condensed 3-tab grouped navigation (Medical Care / Purchases & Visits / My Account) */}
        <GroupedPortalNav
          groups={PATIENT_NAV_GROUPS}
          activeTab={activeTab}
          onTabChange={(id) => { setActiveTab(id); if (id === 'support') fetchSupportData(); }}
          onExternalNav={(path) => navigate(path)}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="hidden">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="face-vitals">Face Vitals</TabsTrigger>
            <TabsTrigger value="health-tracking">Health</TabsTrigger>
            <TabsTrigger value="visits">Visits</TabsTrigger>
            <TabsTrigger value="prescriptions">Prescriptions</TabsTrigger>
            <TabsTrigger value="lab-reports">Lab Reports</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="bills">Bills</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Referral Card */}
            <Card className="bg-gradient-to-r from-teal-600 to-teal-800 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80 flex items-center gap-2">
                      <Gift className="h-4 w-4" /> Your Referral Code
                    </p>
                    <p className="text-3xl font-bold tracking-wider mt-1">{referralCode || 'N/A'}</p>
                    <p className="text-sm opacity-80 mt-2">Share and earn rewards when friends join!</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="icon" onClick={copyReferralCode}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="secondary" size="icon" onClick={shareReferralCode}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Visits */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">Recent Visits</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('visits')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {visits.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No visits yet</p>
                  ) : (
                    <div className="space-y-3">
                      {visits.slice(0, 3).map((visit) => (
                        <div key={visit.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{visit.doctor_name || 'Doctor'}</p>
                            <p className="text-sm text-muted-foreground">{visit.clinic_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">{visit.date}</p>
                            <Badge variant="outline">{visit.soap?.assessment?.diagnosis || 'General'}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Appointments */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">Upcoming Appointments</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('appointments')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {appointments.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground mb-3">No upcoming appointments</p>
                      <Button size="sm" onClick={() => navigate('/patient/appointments/new')}>
                        <Plus className="h-4 w-4 mr-2" /> Book Appointment
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appointments.slice(0, 3).map((appt) => (
                        <div key={appt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{appt.doctor?.name || 'Doctor'}</p>
                            <p className="text-sm text-muted-foreground">{appt.reason}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{appt.date}</p>
                            <p className="text-xs text-muted-foreground">{appt.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-teal-200 hover:bg-teal-50" data-testid="patient-face-vitals-btn" onClick={() => setActiveTab('face-vitals')}>
                <Camera className="h-6 w-6 text-teal-600" />
                <span className="text-center text-xs sm:text-sm">Face Vitals<br/><span className="text-muted-foreground text-xs">(30s Scan)</span></span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/patient/marketplace')}>
                <ShoppingCart className="h-6 w-6" />
                <span className="text-center text-xs sm:text-sm">Marketplace<br/><span className="text-muted-foreground text-xs">(Lab Tests / Medicines)</span></span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/patient/consultation')}>
                <MessageSquare className="h-6 w-6" />
                <span>Book Consultation</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/patient/wallet')}>
                <Wallet className="h-6 w-6" />
                <span>Add Money</span>
              </Button>
            </div>
          </TabsContent>

          {/* Face Vitals Tab */}
          <TabsContent value="face-vitals">
            <PatientFaceVitalsTab patient={patient} />
          </TabsContent>

          <TabsContent value="health-tracking">
            <PatientHealthTracking />
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>My Health Records</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">From:</span>
                      <Input
                        type="date"
                        value={visitsDateFrom}
                        onChange={(e) => setVisitsDateFrom(e.target.value)}
                        className="w-36 h-8 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">To:</span>
                      <Input
                        type="date"
                        value={visitsDateTo}
                        onChange={(e) => setVisitsDateTo(e.target.value)}
                        className="w-36 h-8 text-sm"
                      />
                    </div>
                    {(visitsDateFrom || visitsDateTo) && (
                      <Button variant="ghost" size="sm" onClick={() => { setVisitsDateFrom(''); setVisitsDateTo(''); }}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No visit records found</p>
                ) : (
                  <div className="space-y-4">
                    {visits
                      .filter(visit => {
                        const visitDate = visit.date || (visit.created_at ? visit.created_at.split('T')[0] : '');
                        if (visitsDateFrom && visitDate < visitsDateFrom) return false;
                        if (visitsDateTo && visitDate > visitsDateTo) return false;
                        return true;
                      })
                      .map((visit) => (
                      <div key={visit.id} className="p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold">{visit.doctor_name}</p>
                            <p className="text-sm text-muted-foreground">{visit.clinic_name} | {visit.doctor_specialty}</p>
                            <p className="text-sm mt-2">
                              <span className="font-medium">Diagnosis:</span> {visit.soap?.assessment?.diagnosis || visit.diagnosis?.join(', ') || 'N/A'}
                            </p>
                            {visit.vitals && Object.keys(visit.vitals).length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Vitals: {visit.vitals.bp && `BP: ${visit.vitals.bp}`} {visit.vitals.pulse && `| Pulse: ${visit.vitals.pulse}`}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <Badge className="mb-1">{visit.date || (visit.created_at ? visit.created_at.split('T')[0] : 'N/A')}</Badge>
                            {visit.created_at && (
                              <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(visit.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions">
            <Card>
              <CardHeader>
                <CardTitle>My Prescriptions</CardTitle>
              </CardHeader>
              <CardContent>
                {prescriptions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No prescriptions found</p>
                ) : (
                  <div className="space-y-4">
                    {prescriptions.map((rx) => (
                      <div key={rx.id} className="p-4 border rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                          <div>
                            <p className="font-semibold">{rx.doctor_name}</p>
                            <p className="text-sm text-muted-foreground">{rx.clinic_name}</p>
                            {rx.doctor_specialty && (
                              <p className="text-xs text-teal-600">{rx.doctor_specialty}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge>{rx.date}</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const visitData = {
                                  created_at: rx.created_at || rx.date,
                                  doctor_name: rx.doctor_name,
                                  diagnosis: rx.diagnosis || [],
                                  medications: rx.medications || [],
                                  lab_tests: rx.lab_tests || [],
                                  vitals: rx.vitals || {},
                                  follow_up_date: rx.follow_up_date,
                                  plan: rx.notes,
                                  prescription_barcode: rx.prescription_barcode
                                };
                                const patientData = {
                                  name: rx.patient_name || patient?.name,
                                  phone: rx.patient_phone || patient?.phone,
                                  age: rx.patient_age,
                                  gender: rx.patient_gender,
                                  blood_group: rx.patient_blood_group
                                };
                                openPrescriptionPrint(visitData, patientData, rx.clinic_settings);
                              }}
                              data-testid={`download-prescription-${rx.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </Button>
                            {rx.prescription_barcode && rx.medications?.length > 0 && (
                              <Button
                                size="sm"
                                className="bg-teal-600 hover:bg-teal-700 text-white"
                                onClick={() => navigate(`/patient/marketplace?rx=${rx.prescription_barcode}`)}
                                data-testid={`order-medicines-${rx.id}`}
                              >
                                <ShoppingCart className="h-4 w-4 mr-1" />
                                Order Medicines
                              </Button>
                            )}
                          </div>
                        </div>
                        {rx.diagnosis && rx.diagnosis.length > 0 && (
                          <p className="text-sm mb-2">
                            <span className="font-medium">Diagnosis:</span>{' '}
                            {Array.isArray(rx.diagnosis) ? rx.diagnosis.join(', ') : rx.diagnosis}
                          </p>
                        )}
                        <div className="space-y-2">
                          {rx.medications?.map((med, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm bg-slate-50 p-2 rounded">
                              <Pill className="h-4 w-4 text-purple-600" />
                              <span className="font-medium">{med.name}</span>
                              <span className="text-muted-foreground">- {med.dosage} | {med.frequency} | {med.duration}</span>
                            </div>
                          ))}
                        </div>
                        {rx.follow_up_date && (
                          <p className="text-sm mt-2 text-teal-600">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            Follow-up: {rx.follow_up_date}
                          </p>
                        )}
                        {rx.prescription_barcode && (
                          <p className="text-xs mt-2 text-slate-400 font-mono">
                            Rx Code: {rx.prescription_barcode}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lab Reports Tab */}
          <TabsContent value="lab-reports">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>My Lab Reports</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <Input 
                        type="date" 
                        value={labReportDateFrom} 
                        onChange={(e) => setLabReportDateFrom(e.target.value)}
                        className="w-36 h-9 text-sm"
                        placeholder="From"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input 
                        type="date" 
                        value={labReportDateTo} 
                        onChange={(e) => setLabReportDateTo(e.target.value)}
                        className="w-36 h-9 text-sm"
                        placeholder="To"
                      />
                    </div>
                    {(labReportDateFrom || labReportDateTo) && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setLabReportDateFrom(''); setLabReportDateTo(''); }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {labReports.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No lab reports found</p>
                ) : (
                  <div className="space-y-4">
                    {labReports
                      .filter(report => {
                        const reportDate = report.uploaded_at?.split('T')[0] || report.created_at?.split('T')[0];
                        if (!reportDate) return true;
                        if (labReportDateFrom && reportDate < labReportDateFrom) return false;
                        if (labReportDateTo && reportDate > labReportDateTo) return false;
                        return true;
                      })
                      .map((report) => (
                      <div key={report.id} className="p-4 border rounded-lg flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <TestTube className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{report.report_title || report.test_name || 'Lab Report'}</p>
                            <p className="text-sm text-muted-foreground">{report.lab_name || 'Lab'}</p>
                            <p className="text-xs text-muted-foreground">Patient: {report.patient_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{(report.uploaded_at || report.created_at)?.split('T')[0]}</Badge>
                          {report.file_url && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => window.open(`${API_URL}${report.file_url}`, '_blank')}
                                className="gap-1"
                              >
                                <Eye className="h-4 w-4" /> View
                              </Button>
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = `${API_URL}${report.file_url}/download`;
                                  link.download = report.report_title || 'lab_report';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  toast.success('Download started');
                                }}
                                className="gap-1"
                              >
                                <Download className="h-4 w-4" /> Download
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {labReports.filter(report => {
                      const reportDate = report.uploaded_at?.split('T')[0] || report.created_at?.split('T')[0];
                      if (!reportDate) return true;
                      if (labReportDateFrom && reportDate < labReportDateFrom) return false;
                      if (labReportDateTo && reportDate > labReportDateTo) return false;
                      return true;
                    }).length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No reports found for selected date range</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>My Appointments</CardTitle>
                <Button onClick={() => setShowAppointmentDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Book New
                </Button>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No appointments found</p>
                ) : (
                  <div className="space-y-4">
                    {appointments.map((appt) => (
                      <div key={appt.id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              appt.status === 'rejected' ? 'bg-red-100' :
                              appt.status === 'confirmed' ? 'bg-green-100' :
                              appt.status === 'completed' ? 'bg-blue-100' :
                              'bg-amber-100'
                            }`}>
                              <Calendar className={`h-5 w-5 ${
                                appt.status === 'rejected' ? 'text-red-600' :
                                appt.status === 'confirmed' ? 'text-green-600' :
                                appt.status === 'completed' ? 'text-blue-600' :
                                'text-amber-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-semibold">{appt.doctor?.name || appt.doctor_name || 'Doctor'}</p>
                              <p className="text-sm text-muted-foreground">{appt.clinic_name}</p>
                              <p className="text-sm text-muted-foreground">{appt.reason}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{appt.date}</p>
                            <p className="text-sm text-muted-foreground">{appt.time || appt.time_slot}</p>
                            <Badge className={
                              appt.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              appt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              appt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              appt.status === 'cancelled' ? 'bg-slate-100 text-slate-700' :
                              'bg-amber-100 text-amber-700'
                            }>{appt.status}</Badge>
                            {['requested', 'confirmed', 'scheduled'].includes(appt.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`cancel-appointment-${appt.id}`}
                                onClick={() => handleCancelAppointment(appt.id || appt._id)}
                              >
                                <XCircle className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* Show rejection reason if rejected */}
                        {appt.status === 'rejected' && (appt.rejection_reason || appt.reject_reason) && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                            <p className="text-sm text-red-600">{appt.rejection_reason || appt.reject_reason}</p>
                            {appt.alternative_date && (
                              <p className="text-sm text-teal-600 mt-2">
                                <span className="font-medium">Alternative suggested:</span> {appt.alternative_date} at {appt.alternative_time}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Doctor's Notes */}
                        {appt.notes && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`appt-notes-${appt.id}`}>
                            <p className="text-sm font-medium text-blue-700">Doctor's Notes:</p>
                            <p className="text-sm text-blue-600">{appt.notes}</p>
                          </div>
                        )}
                        
                        {/* Clinic Details */}
                        {(appt.clinic_address || appt.clinic_phone) && (
                          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {appt.clinic_address && (
                              <span className="flex items-center gap-1" data-testid={`appt-clinic-address-${appt.id}`}>
                                <MapPin className="h-3 w-3" /> {appt.clinic_address}
                              </span>
                            )}
                            {appt.clinic_phone && (
                              <span className="flex items-center gap-1" data-testid={`appt-clinic-phone-${appt.id}`}>
                                <Phone className="h-3 w-3" /> {appt.clinic_phone}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Show alternative time if provided */}
                        {appt.status === 'rejected' && appt.alternative_date && !appt.rejection_reason && !appt.reject_reason && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm font-medium text-amber-700">Alternative Time Suggested:</p>
                            <p className="text-sm text-amber-600">{appt.alternative_date} at {appt.alternative_time}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bills Tab - Condensed UPI-pay billing ledger */}
          <TabsContent value="bills">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>My Bills</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {bills.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No bills found</p>
                ) : (
                  <div className="space-y-2.5 sm:space-y-3" data-testid="bills-ledger-list">
                    {bills.map((bill) => {
                      const amountDue = bill.total - (bill.amount_paid || 0);
                      const isPaid = bill.payment_status === 'paid';
                      return (
                        <Card key={bill.id} className="border-slate-200 shadow-none" data-testid={`bill-card-${bill.id}`}>
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-sm text-[#1A1D1F] truncate">{bill.doctor_name || bill.clinic_name || 'Clinic Visit'}</p>
                                <p className="text-xs text-[#4A4D52] truncate">{bill.clinic_name}</p>
                                <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" /> {bill.date || bill.created_at?.split('T')[0]}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[10px] text-slate-400">{isPaid ? 'Paid' : 'Due Amount'}</p>
                                <p className={cn('font-bold text-lg', isPaid ? 'text-slate-400' : 'text-[#1A1D1F]')}>₹{isPaid ? bill.total : amountDue}</p>
                                <Badge className={isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'} data-testid={`bill-status-${bill.id}`}>
                                  {isPaid ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                                  {bill.payment_status}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              {!isPaid && (
                                <Button
                                  className="flex-1 h-10 bg-[#2BB673] hover:bg-[#22925B] text-white font-bold shadow-sm"
                                  onClick={() => handleInstantUpiPay(bill)}
                                  data-testid={`instant-upi-pay-${bill.id}`}
                                >
                                  <QrCode className="h-4 w-4 mr-1.5" /> Instant UPI Pay
                                </Button>
                              )}
                              {!isPaid && (
                                <Button
                                  variant="outline"
                                  className="h-10"
                                  onClick={() => { setSelectedBill(bill); setShowPayBillDialog(true); }}
                                  data-testid={`pay-via-wallet-${bill.id}`}
                                >
                                  <Wallet className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="outline"
                                className="h-10 w-10 shrink-0"
                                onClick={() => openInvoicePrint(bill, bill.clinic_settings)}
                                data-testid={`download-invoice-${bill.id}`}
                                title="Download invoice"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <CardTitle>My Orders</CardTitle>
                    <Button onClick={() => navigate('/patient/marketplace')}>
                      <ShoppingCart className="h-4 w-4 mr-2" /> Shop Now
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by order #, product, vendor..."
                        value={ordersSearch}
                        onChange={(e) => setOrdersSearch(e.target.value)}
                        className="pl-10 h-9"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">From:</span>
                      <Input
                        type="date"
                        value={ordersDateFrom}
                        onChange={(e) => setOrdersDateFrom(e.target.value)}
                        className="w-36 h-9 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">To:</span>
                      <Input
                        type="date"
                        value={ordersDateTo}
                        onChange={(e) => setOrdersDateTo(e.target.value)}
                        className="w-36 h-9 text-sm"
                      />
                    </div>
                    {(ordersDateFrom || ordersDateTo || ordersSearch) && (
                      <Button variant="ghost" size="sm" onClick={() => { setOrdersDateFrom(''); setOrdersDateTo(''); setOrdersSearch(''); }}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No orders yet</p>
                ) : (
                  <div className="space-y-4">
                    {orders
                      .filter(order => {
                        // Date filter
                        const orderDate = order.created_at ? order.created_at.split('T')[0] : '';
                        if (ordersDateFrom && orderDate < ordersDateFrom) return false;
                        if (ordersDateTo && orderDate > ordersDateTo) return false;
                        
                        // Search filter
                        if (ordersSearch) {
                          const searchLower = ordersSearch.toLowerCase();
                          const matchOrder = order.order_number?.toLowerCase().includes(searchLower);
                          const matchVendor = order.items?.some(item => item.vendor_name?.toLowerCase().includes(searchLower));
                          const matchProduct = order.items?.some(item => item.product_name?.toLowerCase().includes(searchLower));
                          const matchDoctor = order.prescribing_doctor_name?.toLowerCase().includes(searchLower);
                          if (!matchOrder && !matchVendor && !matchProduct && !matchDoctor) return false;
                        }
                        return true;
                      })
                      .map((order) => {
                        // Extract unique vendors
                        const vendors = [...new Set(order.items?.map(item => item.vendor_name).filter(Boolean))];
                        return (
                        <div key={order.id} className="p-4 border rounded-lg hover:bg-slate-50">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-semibold text-lg">Order #{order.order_number}</p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Calendar className="h-4 w-4" />
                                <span>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : 'N/A'}</span>
                                <Clock className="h-4 w-4 ml-2" />
                                <span>{order.created_at ? new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-teal-600">₹{order.total?.toLocaleString()}</p>
                              <Badge className={
                                order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>{order.status}</Badge>
                              {['pending', 'confirmed'].includes(order.status) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`cancel-order-${order.id}`}
                                  onClick={() => handleCancelOrder(order.id || order._id)}
                                >
                                  <XCircle className="h-3 w-3 mr-1" /> Cancel Order
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Order Details */}
                          <div className="grid sm:grid-cols-2 gap-4 text-sm border-t pt-3">
                            <div>
                              <p className="text-muted-foreground mb-1">Prescribed By:</p>
                              <p className="font-medium flex items-center gap-1">
                                <Stethoscope className="h-4 w-4 text-teal-600" />
                                {order.prescribing_doctor_name || 'Self'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-1">Vendor(s):</p>
                              <p className="font-medium">
                                {vendors.length > 0 ? vendors.join(', ') : 'N/A'}
                              </p>
                            </div>
                            <div className="sm:col-span-2">
                              <p className="text-muted-foreground mb-1">Products ({order.items?.length || 0}):</p>
                              <div className="flex flex-wrap gap-2">
                                {order.items?.map((item, idx) => (
                                  <Badge key={idx} variant="outline" className="font-normal">
                                    {item.product_name} x{item.quantity}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            {order.delivery_address && (
                              <div className="sm:col-span-2">
                                <p className="text-muted-foreground mb-1">Delivery Address:</p>
                                <p className="text-sm flex items-start gap-1">
                                  <MapPin className="h-4 w-4 mt-0.5" />
                                  {order.delivery_address}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );})}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6 overflow-x-hidden relative">
            {/* Platform Support Card */}
            <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-teal-700">
                  <Building2 className="w-5 h-5" /> 1Clin Platform Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {platformSupport?.support_phone && (
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-testid="platform-support-phone">
                      <Phone className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-500">Phone</p>
                        <p className="font-medium text-sm truncate">{platformSupport.support_phone}</p>
                      </div>
                      <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => window.open(`tel:${platformSupport.support_phone}`)}>Call</Button>
                    </div>
                  )}
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-testid="platform-support-email">
                    <Mail className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-sm truncate">{platformSupport?.support_email || 'support@1clin.com'}</p>
                    </div>
                    <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => window.open(`mailto:${platformSupport?.support_email || 'support@1clin.com'}`)}>Email</Button>
                  </div>
                  {platformSupport?.whatsapp_number && (
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg" data-testid="platform-support-whatsapp">
                      <MessageSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-500">WhatsApp</p>
                        <p className="font-medium text-sm truncate">{platformSupport.whatsapp_number}</p>
                      </div>
                      <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => window.open(`https://wa.me/${platformSupport.whatsapp_number.replace(/\D/g, '')}`)}>Chat</Button>
                    </div>
                  )}
                </div>
                {platformSupport?.business_hours && (
                  <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {platformSupport.business_hours}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Floating chat action trigger */}
            <button
              onClick={() => window.open(platformSupport?.whatsapp_number ? `https://wa.me/${platformSupport.whatsapp_number.replace(/\D/g, '')}` : `mailto:${platformSupport?.support_email || 'support@1clin.com'}`)}
              className="fixed bottom-5 right-5 z-50 bg-[#2BB673] text-white p-3.5 rounded-full shadow-lg hover:scale-105 hover:bg-[#22925B] transition-transform"
              data-testid="floating-support-chat-btn"
              title="Chat with Support"
            >
              <MessageCircle className="w-5 h-5" />
            </button>

            {/* Directory & Tickets */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden">
              {/* Directory */}
              <div className="lg:col-span-2 space-y-4 min-w-0">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Directory</h3>
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="support-directory-search"
                      placeholder="Search doctors, vendors..."
                      value={supportSearch}
                      onChange={e => setSupportSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={supportFilter} onValueChange={setSupportFilter}>
                      <SelectTrigger className="flex-1 h-9" data-testid="support-type-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="doctors">Doctors</SelectItem>
                        <SelectItem value="vendors">Vendors</SelectItem>
                      </SelectContent>
                    </Select>
                    {(supportFilter === 'all' || supportFilter === 'vendors') && (
                      <Select value={supportVendorType} onValueChange={setSupportVendorType}>
                        <SelectTrigger className="flex-1 h-9" data-testid="support-vendor-type-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="pharma">Pharma</SelectItem>
                          <SelectItem value="lab">Lab</SelectItem>
                          <SelectItem value="inventory">Equipment</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredSupportItems().length === 0 && (
                    <Card><CardContent className="py-8 text-center text-muted-foreground">No results found</CardContent></Card>
                  )}
                  {filteredSupportItems().map((item, idx) => (
                    <Card key={item.id || idx} className="hover:shadow-sm transition-shadow" data-testid={`directory-item-${item._type}-${idx}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {item.logo_url ? (
                            <img src={item.logo_url.startsWith('http') ? item.logo_url : `${API_URL}${item.logo_url}`} alt="" className="w-10 h-10 rounded-full object-cover border flex-shrink-0" />
                          ) : (
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${item._type === 'doctor' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                              {item._type === 'doctor' ? <Stethoscope className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                            </div>
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="font-medium text-sm truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {item._type === 'doctor' ? `${item.specialty || 'General'} • ${item.clinic_name || ''}` : `${(item.vendor_type || '').charAt(0).toUpperCase() + (item.vendor_type || '').slice(1)} Vendor`}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px]">{item._type === 'doctor' ? 'Doctor' : 'Vendor'}</Badge>
                              {item.phone && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); window.open(`tel:${item.phone}`); }}>
                                  <Phone className="h-3 w-3" />
                                </Button>
                              )}
                              {item.email && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); window.open(`mailto:${item.email}`); }}>
                                  <Mail className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Support Tickets */}
              <div className="space-y-4 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">My Tickets</h3>
                  <Button size="sm" onClick={() => setShowCreateTicket(true)} data-testid="create-support-ticket-btn">
                    <Plus className="h-4 w-4 mr-1" /> New Ticket
                  </Button>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {supportTickets.length === 0 && (
                    <Card>
                      <CardContent className="py-8 text-center text-muted-foreground">
                        <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No support tickets yet</p>
                        <Button variant="link" size="sm" onClick={() => setShowCreateTicket(true)}>Create one</Button>
                      </CardContent>
                    </Card>
                  )}
                  {supportTickets.map(ticket => (
                    <Card key={ticket.id} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setShowTicketDetail(ticket)} data-testid={`ticket-${ticket.ticket_number}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{ticket.subject}</p>
                            <p className="text-xs text-muted-foreground">{ticket.ticket_number}</p>
                          </div>
                          <Badge variant={ticket.status === 'open' ? 'default' : ticket.status === 'resolved' ? 'outline' : 'secondary'} className="text-xs shrink-0">
                            {ticket.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(ticket.created_at).toLocaleDateString()}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>My Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="font-medium flex items-center gap-2">
                      <User className="h-4 w-4" /> {patient?.name || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4" /> {patient?.phone || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" /> {patient?.email || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Date of Birth</label>
                    <p className="font-medium">{patient?.date_of_birth || 'N/A'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Gender</label>
                    <p className="font-medium capitalize">{patient?.gender || 'N/A'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">Blood Group</label>
                    <p className="font-medium">{patient?.blood_group || 'N/A'}</p>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Address</label>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> {patient?.address || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Button variant="outline" onClick={openEditProfile}>
                    Edit Profile
                  </Button>
                </div>

                {/* Saved Addresses */}
                <div className="pt-4 border-t">
                  <SavedAddresses token={localStorage.getItem('patient_token')} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Book Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={(open) => {
        setShowAppointmentDialog(open);
        if (open) {
          fetchDoctorsForAppointment();
          setSelectedDoctor(null);
          setAppointmentDoctor('');
          setAppointmentDate('');
          setAppointmentTime('');
          setAppointmentReason('');
          setDoctorSearch('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Book New Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Search Bar */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search Doctor/Clinic</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search by doctor name, clinic name, address, or specialty..."
                  value={doctorSearch}
                  onChange={(e) => setDoctorSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchDoctors(doctorSearch)}
                  className="flex-1"
                />
                <Button variant="outline" onClick={() => searchDoctors(doctorSearch)} disabled={searchingDoctors}>
                  {searchingDoctors ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Doctor Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Doctor/Clinic *</label>
              {searchingDoctors ? (
                <div className="text-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-500" />
                  <p className="text-sm text-muted-foreground mt-2">Loading doctors...</p>
                </div>
              ) : doctors.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No doctors found. Try a different search term.</p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                  {doctors.map((doc) => (
                    <div
                      key={doc.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedDoctor?.id === doc.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        setSelectedDoctor(doc);
                        setAppointmentDoctor(doc.id);
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-800">{doc.name}</p>
                          <p className="text-sm text-blue-600">{doc.clinic_name}</p>
                          <p className="text-xs text-slate-500">{doc.specialty || 'General Physician'}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">₹{doc.consultation_fee}</Badge>
                      </div>
                      {doc.address && (
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {doc.address}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{doctors.length} doctors/clinics found</p>
            </div>
            
            {/* Selected Doctor Info with Clinic Timings */}
            {selectedDoctor && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">{selectedDoctor.name}</h4>
                    <p className="text-sm text-blue-600">{selectedDoctor.clinic_name}</p>
                    {selectedDoctor.address && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" /> {selectedDoctor.address}
                      </p>
                    )}
                    {selectedDoctor.phone && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {selectedDoctor.phone}
                      </p>
                    )}
                    {selectedDoctor.consultation_fee > 0 && (
                      <p className="text-sm font-semibold text-teal-700 mt-1" data-testid="doctor-consultation-fee">
                        Consultation Fee: ₹{selectedDoctor.consultation_fee}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Clinic Timings */}
                {selectedDoctor.clinic_timings && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> CLINIC TIMINGS
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                      {selectedDoctor.clinic_timings.filter(t => t.is_open).slice(0, 4).map((timing) => {
                        const times = [`${timing.morning_start}-${timing.morning_end}`, timing.evening_start ? `${timing.evening_start}-${timing.evening_end}` : null, ...(timing.extra_slots || []).map(es => es.start && es.end ? `${es.start}-${es.end}` : null)].filter(Boolean);
                        return (
                          <div key={timing.day} className="text-xs bg-white rounded px-2 py-1">
                            <span className="font-medium capitalize">{timing.day.slice(0, 3)}</span>
                            <span className="text-slate-500 block">{times.join(', ')}</span>
                          </div>
                        );
                      })}
                    </div>
                    {selectedDoctor.clinic_timings.filter(t => !t.is_open).length > 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        Closed: {selectedDoctor.clinic_timings.filter(t => !t.is_open).map(t => t.day.slice(0, 3)).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Date and Time Selection */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Preferred Date *</label>
                <Input
                  type="date"
                  data-testid="appointment-date-input"
                  value={appointmentDate}
                  onChange={(e) => { setAppointmentDate(e.target.value); setAppointmentTime(''); }}
                  min={new Date().toISOString().split('T')[0]}
                />
                {appointmentDate && selectedDoctor && (() => {
                  const timing = getTimingForDay(selectedDoctor, appointmentDate);
                  if (!timing) return <p className="text-xs text-slate-500">Timing info not available</p>;
                  if (!timing.is_open) return <p className="text-xs text-red-500 font-medium">Clinic is closed on this day. Please select another date.</p>;
                  return (() => { const times = [`${timing.morning_start}-${timing.morning_end}`, timing.evening_start ? `${timing.evening_start}-${timing.evening_end}` : null, ...(timing.extra_slots || []).map(es => es.start && es.end ? `${es.start}-${es.end}` : null)].filter(Boolean); return <p className="text-xs text-green-600">Open: {times.join(', ')}</p>; })();
                })()}
              </div>
              
              {/* Time Slot Selection */}
              {appointmentDate && selectedDoctor && (() => {
                const slots = generateTimeSlots(selectedDoctor, appointmentDate);
                if (slots.length === 0) return null;
                
                const morningSlots = slots.filter(s => s.session === 'Morning');
                const eveningSlots = slots.filter(s => s.session === 'Evening');
                const extraSlots = slots.filter(s => s.session !== 'Morning' && s.session !== 'Evening');
                
                return (
                  <div className="space-y-2" data-testid="time-slot-container">
                    <label className="text-sm font-medium">Preferred Time Slot *</label>
                    {morningSlots.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Morning</p>
                        <div className="flex flex-wrap gap-2">
                          {morningSlots.map(s => (
                            <Button
                              key={s.time}
                              type="button"
                              size="sm"
                              variant={appointmentTime === s.time ? 'default' : 'outline'}
                              className={`text-xs h-8 px-3 ${appointmentTime === s.time ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                              onClick={() => setAppointmentTime(s.time)}
                              data-testid={`time-slot-${s.time}`}
                            >
                              {s.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {eveningSlots.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Evening</p>
                        <div className="flex flex-wrap gap-2">
                          {eveningSlots.map(s => (
                            <Button
                              key={s.time}
                              type="button"
                              size="sm"
                              variant={appointmentTime === s.time ? 'default' : 'outline'}
                              className={`text-xs h-8 px-3 ${appointmentTime === s.time ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                              onClick={() => setAppointmentTime(s.time)}
                              data-testid={`time-slot-${s.time}`}
                            >
                              {s.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {extraSlots.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Other</p>
                        <div className="flex flex-wrap gap-2">
                          {extraSlots.map(s => (
                            <Button
                              key={s.time}
                              type="button"
                              size="sm"
                              variant={appointmentTime === s.time ? 'default' : 'outline'}
                              className={`text-xs h-8 px-3 ${appointmentTime === s.time ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                              onClick={() => setAppointmentTime(s.time)}
                              data-testid={`time-slot-${s.time}`}
                            >
                              {s.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason for Visit *</label>
              <Input
                placeholder="Describe your concern..."
                value={appointmentReason}
                onChange={(e) => setAppointmentReason(e.target.value)}
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>Your appointment request will be sent to the doctor for confirmation.</p>
              {selectedDoctor?.consultation_fee > 0 && (
                <p className="mt-1 font-medium">Consultation Fee: ₹{selectedDoctor.consultation_fee}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppointmentDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestAppointment} disabled={submitting || !appointmentDoctor || !appointmentDate || !appointmentReason || !appointmentTime}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfileDialog} onOpenChange={setShowEditProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm font-medium">Full Name</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Date of Birth</label>
                <Input type="date" value={editDob} onChange={(e) => setEditDob(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Gender</label>
                <Select value={editGender} onValueChange={setEditGender}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Blood Group</label>
              <Select value={editBloodGroup} onValueChange={setEditBloodGroup}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Address</label>
              <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Emergency Contact</label>
              <Input value={editEmergencyContact} onChange={(e) => setEditEmergencyContact(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProfileDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Pay Bill Dialog */}
      <Dialog open={showPayBillDialog} onOpenChange={setShowPayBillDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay Invoice</DialogTitle>
          </DialogHeader>
          {selectedBill && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Invoice #</span>
                  <span className="font-medium">{selectedBill.invoice_number}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Doctor</span>
                  <span>{selectedBill.doctor_name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Clinic</span>
                  <span>{selectedBill.clinic_name}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                  <span>Amount Due</span>
                  <span className="text-teal-600">₹{selectedBill.total - (selectedBill.amount_paid || 0)}</span>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg ${wallet.balance >= (selectedBill.total - (selectedBill.amount_paid || 0)) ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  <span>Wallet Balance: ₹{wallet.balance.toFixed(2)}</span>
                </div>
                {wallet.balance < (selectedBill.total - (selectedBill.amount_paid || 0)) && (
                  <p className="text-sm mt-1">Insufficient balance. Please add money to your wallet.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayBillDialog(false)}>Cancel</Button>
            <Button
              onClick={handlePayBill}
              disabled={submitting || !selectedBill || wallet.balance < (selectedBill?.total - (selectedBill?.amount_paid || 0))}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Pay ₹{selectedBill ? (selectedBill.total - (selectedBill.amount_paid || 0)) : 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Support Ticket Dialog */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input data-testid="ticket-subject" value={ticketSubject} onChange={e => setTicketSubject(e.target.value)} placeholder="Brief description of your issue" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Issue Category *</Label>
                <Select value={ticketCategory} onValueChange={setTicketCategory}>
                  <SelectTrigger data-testid="ticket-category"><SelectValue placeholder="Select an issue category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reschedule_appointment">Reschedule Appointment</SelectItem>
                    <SelectItem value="prescription_download">Download Prescription Link</SelectItem>
                    <SelectItem value="payment_wallet">Payment / Wallet Discrepancy</SelectItem>
                    <SelectItem value="technical_help">General Technical Help</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={ticketPriority} onValueChange={setTicketPriority}>
                  <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea data-testid="ticket-message" value={ticketMessage} onChange={e => setTicketMessage(e.target.value)} placeholder="Describe your issue in detail..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
            <Button data-testid="submit-ticket-btn" onClick={handleCreateSupportTicket} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!showTicketDetail} onOpenChange={() => setShowTicketDetail(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showTicketDetail?.subject}
              <Badge variant={showTicketDetail?.status === 'open' ? 'default' : showTicketDetail?.status === 'resolved' ? 'outline' : 'secondary'} className="text-xs">
                {showTicketDetail?.status}
              </Badge>
            </DialogTitle>
            <p className="text-xs text-muted-foreground">{showTicketDetail?.ticket_number} • {showTicketDetail?.created_at ? new Date(showTicketDetail.created_at).toLocaleString() : ''}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm">{showTicketDetail?.message}</p>
            </div>
            {showTicketDetail?.responses?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Responses</p>
                {showTicketDetail.responses.map((r, i) => (
                  <div key={r.id || i} className={`p-3 rounded-lg text-sm ${r.is_admin ? 'bg-teal-50 border-l-2 border-teal-500' : 'bg-slate-50'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-xs">{r.user_name || (r.is_admin ? '1Clin Support' : 'You')}</span>
                      <span className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p>{r.message}</p>
                  </div>
                ))}
              </div>
            )}
            {showTicketDetail?.status !== 'closed' && showTicketDetail?.status !== 'resolved' && (
              <div className="flex gap-2">
                <Input value={ticketReply} onChange={e => setTicketReply(e.target.value)} placeholder="Type your reply..." className="flex-1" data-testid="ticket-reply-input" />
                <Button size="icon" onClick={() => handleReplyTicket(showTicketDetail?.id)} data-testid="ticket-reply-send">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
